import React, { useMemo, useRef, useState } from 'react';
import { Empty, Select, SelectProps, Spin } from 'antd';
import { debounce } from 'lodash';

export interface LinkedArticlesSelectProps<ValueType>
    extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
}

const LinkedArticlesSelect = <
    ValueType extends { key?: string; label: React.ReactNode; value: string | number }
>({
    fetchOptions,
    debounceTimeout = 800,
    ...props
}: LinkedArticlesSelectProps<ValueType>) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const fetchRef = useRef(0);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            if (!value) return;

            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }

                setOptions(newOptions);
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={
                fetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            }
            {...props}
            options={options}
        />
    );
};

export default LinkedArticlesSelect;
